'use client';

import React, { type HTMLAttributes, type ReactNode, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { animated, useSpring } from 'react-spring';
import customCss from '@/app/components/hotel-detail/css/customCss';

type ScrollableHorizontalProps = {
  mobileOnly?: boolean;
  isMouseDown?: boolean;
  children: ReactNode;
  containerRef?: React.RefObject<HTMLDivElement | null>;
} & HTMLAttributes<HTMLDivElement>;

type ScrollableHorizontalWithDotsProps = {
  mobileOnly?: boolean;
  children: ReactNode;
  slides: number;
  gap?: number;
  showOverlay?: boolean;
  scrollSnapEnabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
} & HTMLAttributes<HTMLDivElement>;

type DotsNavigationProps = {
  count: number;
  selected: number;
  onClickHandler?: (page: number) => void;
};

export const MainContainer = styled.div<{ $mobileOnly?: boolean; $isMouseDown?: boolean }>`
  overflow-x: auto;
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */

  ${({ $isMouseDown }) =>
    $isMouseDown &&
    `& * {
          pointer-events: none;
    }`}
  & img {
    user-drag: none;
  }

  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

  ${({ $mobileOnly }) =>
    $mobileOnly
      ? css`
          @media ${customCss.breakpoints.sm} {
            overflow-x: initial;
          }
        `
      : ''}
`;

const DotsContainer = styled.div<{ $selected: number }>`
  padding: 16px 0;
  display: flex;
  justify-content: center;
  gap: 0;
  align-items: center;

  button {
    cursor: pointer;
    position: relative;
    border: 0;
    /* Minimum 24px touch target with visual dot centered inside */
    min-width: 24px;
    min-height: 24px;
    padding: 0;
    margin: 0 -8px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;

    /* Visual dot indicator */
    &::before {
      content: '';
      display: block;
      height: 4px;
      width: 4px;
      border-radius: 50%;
      background-color: ${(p) => p.theme.commons.colors.gray200};
    }
  }

  button:nth-child(${({ $selected }) => $selected + 1})::before {
    background-color: ${(p) => p.theme.commons.colors.gray800};
    height: 6px;
    width: 6px;
  }
`;

function DotsNavigation(props: DotsNavigationProps) {
  const { count, selected, onClickHandler } = props;

  return count ? (
    <DotsContainer $selected={selected}>
      {Array(count)
        .fill(0)
        .map((_, i) => (
          // eslint-disable-next-line jsx-a11y/control-has-associated-label, react/no-array-index-key
          <button key={i} type="button" onClick={() => onClickHandler && onClickHandler(i)} />
        ))}
    </DotsContainer>
  ) : null;
}

function ScrollableHorizontal(props: ScrollableHorizontalProps) {
  const { mobileOnly, children, isMouseDown, containerRef, ...rest } = props;

  return (
    <MainContainer {...rest} ref={containerRef} $mobileOnly={mobileOnly} $isMouseDown={isMouseDown}>
      {children}
    </MainContainer>
  );
}

export const ScrollableHorizontalStyled = styled(animated(ScrollableHorizontal))<{
  $gap: number;
  $mobileOnly?: boolean;
  $slidesByPage: number;
  $isScrollSnapEnabled: boolean;
}>`
  display: flex;
  column-gap: ${({ $gap }) => $gap}px;
  scroll-snap-type: ${({ $isScrollSnapEnabled }) => ($isScrollSnapEnabled ? 'x mandatory' : '')};
  scroll-padding-left: 16px;

  ${({ $slidesByPage }) =>
    $slidesByPage > 1
      ? `> *:nth-child(${$slidesByPage}n + 1) {
              scroll-snap-align: start;
              scroll-snap-stop: always;
            }`
      : `> * {
              scroll-snap-align: center;
            }`};

  @media ${customCss.breakpoints.sm} {
    scroll-padding-left: 0;
  }
`;

export const OverlayLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 60px;
  background: linear-gradient(
    to left,
    transparent 0%,
    ${({ theme }) => theme.commons.colors.gray50} 65%,
    ${({ theme }) => theme.commons.colors.gray50} 100%
  );
`;

export const OverlayRight = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 60px;
  background: linear-gradient(
    to right,
    transparent 0%,
    ${({ theme }) => theme.commons.colors.gray50} 65%,
    ${({ theme }) => theme.commons.colors.gray50} 100%
  );
`;

function runCallbackOnScrollAnimationEnd(element: Element, callback: () => void) {
  let oldScrollLeft = element.scrollLeft;

  const isAnimationEndedTimer = setInterval(() => {
    if (oldScrollLeft === element.scrollLeft) {
      callback();
      clearInterval(isAnimationEndedTimer);
    } else {
      oldScrollLeft = element.scrollLeft;
    }
  }, 100);
}

/**
 *
 * The dots are calculated based on the container width and the number of elements to show.
 * The param slides is the number of elements to show.
 * The param gap is the number of pixels between elements.
 *
 * First thing is calculate the space needed for every element to know how many elements fit
 * in the available space in the container, getting the number of pages and number of elements by page.
 *
 * The css scroll snap is calculated based on the numbers of elements by page, making the scroll
 * stopping on every page or on every element in the case of showing only one element per page.
 *
 * Based on the left scroll position is calculated the page index to know which dot to highlight.
 *
 */
export const ScrollableHorizontalWithDots = (props: ScrollableHorizontalWithDotsProps) => {
  const {
    mobileOnly,
    children,
    slides,
    gap = 16,
    showOverlay = false,
    scrollSnapEnabled = false,
    containerRef,
    ...rest
  } = props;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesByPage, setSlidesByPage] = useState(0);
  const [pages, setPages] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [slidesFullyShownOnPage, setSlidesFullyShownOnPage] = useState(false);
  const [scrollEnd, setScrollEnd] = useState(false);
  const [scrollStart, setScrollStart] = useState(false);

  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isScrollSnapEnabled, setIsScrollSnapEnabled] = useState(scrollSnapEnabled);

  const [_unused, springApi] = useSpring(() => ({
    scroll: 0,
    config: { mass: 1, tension: 200, friction: 30 },
  }));

  const getCurrentIndex = useCallback(
    (container: Element) =>
      slidesByPage === 1
        ? Math.ceil(container.scrollLeft / (slideWidth + gap))
        : Math.floor(container.scrollLeft / ((slideWidth + gap) * slidesByPage)),
    [slidesByPage, slideWidth, gap]
  );

  useEffect(() => {
    const container = containerRef?.current as Element | null;
    if (!container) return;

    function checkSliderWidth() {
      if (!container) return;

      const padding =
        parseFloat(window.getComputedStyle(container).getPropertyValue('padding-left')) +
        parseFloat(window.getComputedStyle(container).getPropertyValue('padding-right'));

      const cardWidth = (container.scrollWidth - padding - (slides - 1) * gap) / slides;
      const nSlidesByPage = Math.round(container.clientWidth / (cardWidth + gap));

      setPages(nSlidesByPage ? Math.ceil(slides / nSlidesByPage) : 0);
      setSlidesByPage(nSlidesByPage);
      setSlideWidth(cardWidth);
      setSlidesFullyShownOnPage(container.clientWidth === Math.ceil((cardWidth + gap) * nSlidesByPage - gap));
    }

    checkSliderWidth();
    window.addEventListener('resize', checkSliderWidth);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', checkSliderWidth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = containerRef?.current as Element | null;
    if (!container) return;

    function onScroll() {
      if (!container) return;

      const maxLeft = container.scrollWidth - container.clientWidth;
      const maxScroll = Math.floor(container.scrollLeft) >= maxLeft - 1;

      // When the slides are 1 by 1, every slide is aligned to the center, in the other cases the slides
      // are aligned to the left
      const currentIndex = getCurrentIndex(container);

      // Sometimes the last page it is not a full page, it could be only one more card or a partial part of a card,
      // in that case we use the total number of pages to highlight the last dot.
      setCurrentSlide(maxScroll ? pages - 1 : currentIndex);

      if (maxScroll) {
        setScrollEnd(true);
      } else if (scrollEnd) {
        setScrollEnd(false);
      }

      if (container.scrollLeft === 0) {
        setScrollStart(true);
      } else {
        setScrollStart(false);
      }
    }

    onScroll();
    container.addEventListener('scroll', onScroll, true);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', onScroll, true);
    };
  }, [pages, slidesByPage, slideWidth, slides, slidesFullyShownOnPage, gap, scrollEnd, getCurrentIndex]);

  const onMouseMoveHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.buttons === 1) {
      setIsMouseDown(true);
      setIsScrollSnapEnabled(false);
    }
  };

  const mouseDownHandler = (e: React.MouseEvent) => {
    if (slides <= slidesByPage) return;

    const el = containerRef?.current;
    if (!el) return;
    const { offsetLeft, scrollLeft } = el;
    const startX = e.pageX - offsetLeft;
    const onMove = (ev: MouseEvent) => {
      el.scrollLeft = scrollLeft - (ev.pageX - offsetLeft - startX);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const resetMouseDown = () => {
    setIsMouseDown(false);
  };

  const dotOnClickHandler = (page: number) => {
    const container = containerRef?.current as Element | null;
    if (!container) return;

    setIsScrollSnapEnabled(false);

    springApi.start({
      from: { scroll: container.scrollLeft },
      to: { scroll: (slideWidth + gap) * slidesByPage * page },
      onChange: ({ value }) => {
        if (containerRef?.current) {
          containerRef.current.scrollLeft = value.scroll;
        }
      },
    });

    runCallbackOnScrollAnimationEnd(container, () => {
      if (scrollSnapEnabled) {
        setIsScrollSnapEnabled(true);
      }
    });
  };

  return (
    <div
      css={`
        position: relative;
      `}
      {...rest}
    >
      <ScrollableHorizontalStyled
        className="scrollContainer"
        containerRef={containerRef}
        $gap={gap}
        $mobileOnly={mobileOnly}
        $slidesByPage={slidesByPage}
        isMouseDown={isMouseDown}
        $isScrollSnapEnabled={isScrollSnapEnabled}
        onMouseDown={mouseDownHandler}
        onMouseLeave={resetMouseDown}
        onMouseMove={onMouseMoveHandler}
        onMouseUp={resetMouseDown}
      >
        {children}
      </ScrollableHorizontalStyled>
      {showOverlay && !slidesFullyShownOnPage && !scrollEnd && <OverlayRight className="overlayRight" />}
      {showOverlay && !slidesFullyShownOnPage && !scrollStart && <OverlayLeft className="overlayLeft" />}
      {pages > 1 ? <DotsNavigation count={pages} selected={currentSlide} onClickHandler={dotOnClickHandler} /> : null}
    </div>
  );
};

ScrollableHorizontalWithDots.displayName = 'ScrollableHorizontalWithDots';

export default ScrollableHorizontal;
